import Http from "../../Http";
import { BASE_URL } from "../../Helper";

/** Normalize a raw theme entry from the backend into a shape the UI can render directly */
const normalizeTheme = (raw) => ({
  themeId: raw.id ?? raw.themeId,
  key: raw.key || raw.themeKey,
  name: raw.name,
  dark: raw.dark,
  light: raw.light,
  active: !!(raw.active ?? raw.isActive),
  isDefaultForOrg: !!(raw.isDefaultForOrg ?? raw.isDefault ?? raw.default),
});

const extractList = (data) =>
  Array.isArray(data)
    ? data
    : data?.themes || data?.orgThemes || data?.content || [];

// Full theme catalogue — GET /master/theme/v1
export const getAllThemes = async () => {
  try {
    const res = await Http.get(`${BASE_URL}theme/v1`, {
      params: {
        fetchAllDefaultTheme: true,
        pageNo: 1,
        recordsPerPage: 100,
      },
    });
    return extractList(res?.data?.data).map(normalizeTheme);
  } catch (error) {
    console.error("Theme API Error:", error);
    throw error?.response?.data || error;
  }
};

// Bulk/filtered org-theme list — GET /master/org_theme/v1. Use getOrgThemeByOrganizationId for a single org.
export const getOrgThemes = async ({
  organizationId,
  organizationIds,
  themeIds,
  isActive,
  isDefaultForOrg,
  fetchDefaultThemes,
  fetchAllDefaultTheme,
  pageNo,
  recordsPerPage,
} = {}) => {
  const params = {};
  const orgIds =
    organizationIds ?? (organizationId != null ? [organizationId] : undefined);
  if (orgIds != null) params.organizationIds = orgIds;
  if (themeIds != null) params.themeIds = themeIds;
  if (isActive != null) params.isActive = isActive;
  if (isDefaultForOrg != null) params.isDefaultForOrg = isDefaultForOrg;
  if (fetchDefaultThemes != null) params.fetchDefaultThemes = fetchDefaultThemes;
  if (fetchAllDefaultTheme != null) params.fetchAllDefaultTheme = fetchAllDefaultTheme;
  if (pageNo != null) params.pageNo = pageNo;
  if (recordsPerPage != null) params.recordsPerPage = recordsPerPage;

  try {
    const res = await Http.get(`${BASE_URL}org_theme/v1`, { params });
    return res?.data?.data?.orgThemes || [];
  } catch (error) {
    console.error("Theme API Error:", error);
    throw error?.response?.data || error;
  }
};

// Raw org-theme mapping for a single org — GET /master/org_theme/v1/{organizationId}. Unnormalized, since some callers need fields beyond theme colors (orgName, etc.)
export const getOrgThemeByOrganizationId = async (organizationId) => {
  try {
    const res = await Http.get(`${BASE_URL}org_theme/v1/${organizationId}`);
    const data = res?.data?.data;
    const list = extractList(data);
    if (list.length) {
      // isActive just means "assigned/enabled for this org" — every theme mapping
      // can carry isActive: true. isDefaultForOrg is the one flag marking which
      // theme is actually applied for the org, so it must be checked first.
      return (
        list.find((t) => t.isDefaultForOrg ?? t.isDefault ?? t.default) ||
        list.find((t) => t.active ?? t.isActive) ||
        list[0]
      );
    }
    if (data && typeof data === "object") return data;
    return null;
  } catch (error) {
    console.error("Theme API Error:", error);
    throw error?.response?.data || error;
  }
};

// Active org theme, normalized to match getAllThemes() so it can be matched against the catalogue
export const getActiveOrgTheme = async (organizationId) => {
  const raw = await getOrgThemeByOrganizationId(organizationId);
  return raw ? normalizeTheme(raw) : null;
};

// Assign/update the theme selected for an organization — POST /master/org_theme/v1
export const assignOrgTheme = async ({
  organizationId,
  themeId,
  setAsDefault = true,
}) => {
  try {
    const res = await Http.post(`${BASE_URL}org_theme/v1`, {
      organizationId,
      themeId,
      setAsDefault,
    });
    return res?.data;
  } catch (error) {
    console.error("Theme API Error:", error);
    throw error?.response?.data || error;
  }
};
